import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, Navigate, Route, Routes, useNavigate, useParams, useSearchParams } from "react-router";
import "./index.css";
import { PERIOD_LABELS, formatDayLong, rangeFor, shiftAnchor, todayISO, type Period } from "./dates";
import type { Client, Entry, EntryInput } from "./db";
import { Analytics } from "./Analytics";
import { api, logout } from "./api";
import { downloadCsv } from "./csv";
import { AccountPanel, useMe } from "./Login";
import { DEFAULT_ACCENT, downloadReportPdf, groupByClient, groupByDate } from "./pdf";
import { anchorFromParam, periodFromSlug, reportPath } from "./route";

type GroupBy = "day" | "client";

const emptyForm = (client_id = 0): EntryInput => ({ date: todayISO(), client_id, title: "", description: "" });


export function App() {
  const today = reportPath("daily", todayISO());
  return (
    <Routes>
      <Route path="/" element={<Navigate to={today} replace />} />
      <Route path="/analiz" element={<Analytics />} />
      <Route path="/:donem/:tarih?" element={<ReportRoute />} />
    </Routes>
  );
}

// /gunluk/2026-09-22, /haftalik/2026-09-21, /aylik/2026-09 — ?musteri=3&grup=musteri
function ReportRoute() {
  const { donem, tarih } = useParams();
  const [searchParams] = useSearchParams();
  const period = periodFromSlug(donem);
  if (!period) return <Navigate to="/" replace />;

  const anchor = anchorFromParam(tarih);
  const search = searchParams.toString();
  const canonical = reportPath(period, anchor ?? todayISO(), search);
  // Eksik/geçersiz tarih ya da dönem başına denk gelmeyen tarih → düzgün adrese yönlendir
  if (!anchor || `/${donem}/${tarih}${search ? `?${search}` : ""}` !== canonical) {
    return <Navigate to={canonical} replace />;
  }
  return <Report period={period} anchor={anchor} />;
}

function Report({ period, anchor }: { period: Period; anchor: string }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.toString();
  const groupBy: GroupBy = searchParams.get("grup") === "musteri" ? "client" : "day";
  // 0 = tüm müşteriler; PDF sadece filtrelenmiş kayıtları içerir
  const clientFilter = Number(searchParams.get("musteri")) || 0;
  const query = (searchParams.get("ara") ?? "").trim();
  const [results, setResults] = useState<Entry[] | null>(null);
  const [backupModal, setBackupModal] = useState(false);
  const [accountModal, setAccountModal] = useState(false);
  const me = useMe()?.me;
  const [entries, setEntries] = useState<Entry[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);

  const [entryModal, setEntryModal] = useState(false);
  const [clientsModal, setClientsModal] = useState(false);
  const [form, setForm] = useState<EntryInput>(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // null = listeden seç, string = formdan yeni müşteri oluştur
  const [newClientName, setNewClientName] = useState<string | null>(null);
  const lastClientId = useRef(0);

  const range = useMemo(() => rangeFor(period, anchor), [period, anchor]);
  const pathFor = (p: Period, a: string) => reportPath(p, a, search);

  const setParam = useCallback(
    (key: string, value: string | null) =>
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          if (value) next.set(key, value);
          else next.delete(key);
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  const setClientFilter = (id: number) => setParam("musteri", id ? String(id) : null);
  const setGroupBy = (g: GroupBy) => setParam("grup", g === "client" ? "musteri" : null);

  useEffect(() => {
    document.title = `${PERIOD_LABELS[period]} · ${range.label} — Çalışma Raporu`;
  }, [period, range.label]);

  const loadClients = useCallback(async () => {
    const list = await api<Client[]>("/api/clients");
    setClients(list);
  }, []);

  // Silinen (ya da adreste olmayan) müşteri filtrede kaldıysa filtreyi kaldır
  useEffect(() => {
    if (clientFilter && clients.length && !clients.some(c => c.id === clientFilter)) setParam("musteri", null);
  }, [clientFilter, clients, setParam]);

  // Hızlı gezinmede geç dönen eski dönem cevabı yeni listenin üstüne yazmasın
  const entriesReq = useRef(0);
  const loadEntries = useCallback(async () => {
    const req = ++entriesReq.current;
    const list = await api<Entry[]>(`/api/entries?from=${range.from}&to=${range.to}`);
    if (req === entriesReq.current) setEntries(list);
  }, [range.from, range.to]);

  // Arama tüm tarihlerde yapılır; adres: ?ara=KBYS
  const searchReq = useRef(0);
  const loadResults = useCallback(async () => {
    const req = ++searchReq.current;
    const list = query ? await api<Entry[]>(`/api/search?q=${encodeURIComponent(query)}`) : null;
    if (req === searchReq.current) setResults(list);
  }, [query]);

  const reload = useCallback(async () => {
    try {
      await Promise.all([loadClients(), loadEntries(), loadResults()]);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [loadClients, loadEntries, loadResults]);

  useEffect(() => {
    reload();
  }, [reload]);

  // ---- Kayıt modalı ----

  const openNewEntry = () => {
    setEditingId(null);
    // Filtredeki ya da son kullanılan müşteri önceden seçili gelir
    const last = clients.some(c => c.id === lastClientId.current) ? lastClientId.current : 0;
    setForm(emptyForm(clientFilter || last || clients[0]?.id || 0));
    setNewClientName(clients.length === 0 ? "" : null);
    setFormError(null);
    setEntryModal(true);
  };

  const openEditEntry = (entry: Entry) => {
    setEditingId(entry.id);
    setForm({ date: entry.date, client_id: entry.client_id, title: entry.title, description: entry.description });
    setNewClientName(null);
    setFormError(null);
    setEntryModal(true);
  };

  // Tekrarlanan iş: aynı müşteri/başlık/açıklama, tarih bugün
  const openCopyEntry = (entry: Entry) => {
    setEditingId(null);
    setForm({ date: todayISO(), client_id: entry.client_id, title: entry.title, description: entry.description });
    setNewClientName(null);
    setFormError(null);
    setEntryModal(true);
  };

  const submitEntry = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      let client_id = form.client_id;
      // Formdan yeni müşteri yazıldıysa önce müşteriyi oluştur
      if (newClientName !== null) {
        const created = await api<Client>("/api/clients", {
          method: "POST",
          body: JSON.stringify({ name: newClientName }),
        });
        client_id = created.id;
        setForm(f => ({ ...f, client_id }));
        setNewClientName(null);
      }
      const body = JSON.stringify({ ...form, client_id });
      if (editingId) {
        await api(`/api/entries/${editingId}`, { method: "PUT", body });
      } else {
        await api("/api/entries", { method: "POST", body });
      }
      lastClientId.current = client_id;
      // Kaydedilen günün raporu ekranda görünsün
      navigate(pathFor(period, form.date));
      setEntryModal(false);
      setEditingId(null);
    } catch (err) {
      setFormError((err as Error).message);
    } finally {
      setSaving(false);
      await reload();
    }
  };

  const removeEntry = async (entry: Entry) => {
    if (!window.confirm(`"${entry.title}" kaydı silinsin mi?`)) return;
    try {
      await api(`/api/entries/${entry.id}`, { method: "DELETE" });
      await reload();
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const visible = clientFilter ? entries.filter(e => e.client_id === clientFilter) : entries;
  const days = groupByDate(visible);
  const clientGroups = groupByClient(visible);
  const periodClients = groupByClient(entries);
  const clientById = new Map(clients.map(c => [c.id, c]));
  const filteredName = clientById.get(clientFilter)?.name;

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      // Tek müşteri seçiliyse PDF o müşterinin rengi ve logosuyla çıkar
      const c = clients.find(c => c.id === clientFilter);
      await downloadReportPdf(period, range, visible, c ? { color: c.color, logoUrl: logoUrl(c) } : {});
    } catch (err) {
      setError(`PDF oluşturulamadı: ${(err as Error).message}`);
    } finally {
      setPdfBusy(false);
    }
  };

  const downloadCsvFile = () => {
    const suffix = period === "daily" ? range.from : `${range.from}_${range.to}`;
    const who = filteredName ? `-${filteredName.replace(/[^\p{L}\p{N}]+/gu, "-")}` : "";
    downloadCsv(`rapor-${period}-${suffix}${who}.csv`, visible);
  };

  const rowProps = { onEdit: openEditEntry, onCopy: openCopyEntry, onDelete: removeEntry };

  const set = (key: "date" | "title" | "description") => (e: { target: { value: string } }) =>
    setForm(f => ({ ...f, [key]: e.target.value }));

  return (
    <div className="min-h-screen">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-4">
          <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-blue-700 text-sm font-bold text-white">
            R
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold leading-tight">Çalışma Raporu</h1>
            <p className="truncate text-sm text-slate-500">Bugün ne yaptım?</p>
          </div>
          <Link to="/analiz" className="btn-secondary">
            Analiz
          </Link>
          <button onClick={() => setBackupModal(true)} className="btn-secondary max-sm:hidden">
            Yedek
          </button>
          <button onClick={() => setClientsModal(true)} className="btn-secondary">
            Müşteriler
          </button>
          <button onClick={openNewEntry} className="btn-primary">
            + Yeni kayıt
          </button>
          {me?.auth && (
            <button onClick={() => setAccountModal(true)} className="btn-ghost text-sm max-sm:hidden" title={me.user}>
              Hesap
            </button>
          )}
          <button onClick={logout} className="btn-ghost text-sm max-sm:hidden" title="Çıkış yap">
            Çıkış
          </button>
        </div>
      </header>

      <Modal open={entryModal} onClose={() => setEntryModal(false)} title={editingId ? "Kaydı düzenle" : "Yeni kayıt"}>
        <form onSubmit={submitEntry} className="space-y-4">
          {formError && <ErrorBox>{formError}</ErrorBox>}
          <Field label="Tarih">
            <input type="date" required value={form.date} onChange={set("date")} className="input" />
          </Field>
          <Field
            label="Müşteri"
            action={
              clients.length > 0 && (
                <button
                  type="button"
                  onClick={() => setNewClientName(n => (n === null ? "" : null))}
                  className="text-xs font-medium text-blue-700 hover:underline"
                >
                  {newClientName === null ? "+ Yeni müşteri" : "Listeden seç"}
                </button>
              )
            }
          >
            {newClientName === null ? (
              <select
                required
                value={form.client_id || ""}
                onChange={e => setForm(f => ({ ...f, client_id: Number(e.target.value) }))}
                className="input"
              >
                <option value="" disabled>
                  Müşteri seçin
                </option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            ) : (
              <input
                required
                placeholder="Örn. karatay-belediyesi"
                value={newClientName}
                onChange={e => setNewClientName(e.target.value)}
                className="input"
              />
            )}
          </Field>
          <Field label="Başlık">
            <input
              required
              data-autofocus
              placeholder="Ne yaptın?"
              value={form.title}
              onChange={set("title")}
              className="input"
            />
          </Field>
          <Field label="Açıklama">
            <textarea
              rows={5}
              placeholder="Detaylar, sonuç, notlar…"
              value={form.description}
              onChange={set("description")}
              className="input resize-y"
            />
          </Field>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setEntryModal(false)} className="btn-ghost text-sm">
              Vazgeç
            </button>
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? "Kaydediliyor…" : editingId ? "Güncelle" : "Kaydet"}
            </button>
          </div>
        </form>
      </Modal>

      <Modal open={clientsModal} onClose={() => setClientsModal(false)} title="Müşteriler">
        <ClientManager clients={clients} onChange={reload} />
      </Modal>

      <Modal open={accountModal} onClose={() => setAccountModal(false)} title="Hesap">
        <AccountPanel key={String(accountModal)} onDone={() => setAccountModal(false)} />
      </Modal>

      <Modal open={backupModal} onClose={() => setBackupModal(false)} title="Yedek">
        <BackupPanel onRestored={reload} />
      </Modal>

      <main className="mx-auto max-w-5xl space-y-4 px-4 py-6">
        {error && (
          <div className="flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            <span>{error}</span>
            <button onClick={() => setError(null)} className="font-medium">
              Kapat
            </button>
          </div>
        )}

        <SearchBox value={query} onChange={q => setParam("ara", q || null)} />

        {results ? (
          <>
            <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
              <p className="min-w-0 truncate text-sm">
                <strong>“{query}”</strong> için {results.length}
                {results.length === 200 && "+"} sonuç <span className="text-slate-500">· tüm tarihler</span>
              </p>
              <button onClick={() => setParam("ara", null)} className="btn-ghost shrink-0 text-sm">
                Aramayı temizle
              </button>
            </div>
            {results.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center text-slate-500">
                Eşleşen kayıt bulunamadı.
              </div>
            ) : (
              groupByDate(results).map(([date, list]) => (
                <Group key={date} title={formatDayLong(date)} count={list.length}>
                  {list.map(entry => (
                    <EntryRow key={entry.id} entry={entry} meta={entry.client_name} {...rowProps} />
                  ))}
                </Group>
              ))
            )}
          </>
        ) : (
          <>
        <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <nav className="inline-flex rounded-lg bg-slate-100 p-1">
              {(Object.keys(PERIOD_LABELS) as Period[]).map(p => (
                <Link key={p} to={pathFor(p, anchor)} className={segmentClass(p === period)}>
                  {PERIOD_LABELS[p]}
                </Link>
              ))}
            </nav>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={clientFilter}
                onChange={e => setClientFilter(Number(e.target.value))}
                className="input w-auto max-w-56 py-1.5"
                aria-label="Müşteri filtresi"
              >
                <option value={0}>Tüm müşteriler</option>
                {clients.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <button onClick={downloadCsvFile} disabled={visible.length === 0} className="btn-secondary py-1.5">
                CSV
              </button>
              <button onClick={downloadPdf} disabled={pdfBusy} className="btn-primary">
                {pdfBusy ? "Hazırlanıyor…" : "PDF indir"}
              </button>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <Link to={pathFor(period, shiftAnchor(period, anchor, -1))} className="btn-icon" aria-label="Önceki">
              ‹
            </Link>
            <Link to={pathFor(period, shiftAnchor(period, anchor, 1))} className="btn-icon" aria-label="Sonraki">
              ›
            </Link>
            <h2 className="min-w-0 flex-1 truncate text-lg font-semibold">{range.label}</h2>
            <Link to={pathFor(period, todayISO())} className="btn-ghost text-sm">
              Bugün
            </Link>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-3">
            <Stat label="Kayıt" value={visible.length} />
            <Stat label="Çalışılan gün" value={days.length} />
            <Stat label="Müşteri" value={clientGroups.length} />
          </div>

          {periodClients.length > 1 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {periodClients.map(g => {
                const color = clientById.get(g.id)?.color || DEFAULT_ACCENT;
                const active = clientFilter === g.id;
                return (
                  <button
                    key={g.id}
                    onClick={() => setClientFilter(active ? 0 : g.id)}
                    className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition"
                    style={
                      active
                        ? { backgroundColor: color, borderColor: color, color: "#fff" }
                        : { borderColor: `${color}40`, backgroundColor: `${color}14`, color: "#1e293b" }
                    }
                  >
                    {!active && <span className="size-2 rounded-full" style={{ backgroundColor: color }} />}
                    {g.name} · {g.entries.length}
                  </button>
                );
              })}
            </div>
          )}

          <p className="mt-3 text-xs text-slate-500">
            {filteredName ? (
              <>
                PDF yalnızca <strong>{filteredName}</strong> kayıtlarını içerir.{" "}
              </>
            ) : (
              <>PDF bu dönemdeki tüm müşterilerin kayıtlarını içerir. </>
            )}
            Müşteri adı PDF'te yazmaz.
          </p>
        </div>

        {visible.length > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-500">Grupla</span>
            <Segmented
              value={groupBy}
              onChange={setGroupBy}
              options={[
                ["day", "Güne göre"],
                ["client", "Müşteriye göre"],
              ]}
            />
          </div>
        )}

        {visible.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-12 text-center text-slate-500">
            {clients.length === 0
              ? "Henüz müşteri yok. “Müşteriler”den ekleyebilir ya da ilk kaydı girerken oluşturabilirsin."
              : "Bu dönemde kayıt yok. Sağ üstteki “Yeni kayıt” ile ekleyebilirsin."}
          </div>
        ) : groupBy === "day" ? (
          days.map(([date, list]) => (
            <Group key={date} title={formatDayLong(date)} count={list.length}>
              {list.map(entry => (
                <EntryRow
                  key={entry.id}
                  entry={entry}
                  meta={entry.client_name}
                  {...rowProps}
                />
              ))}
            </Group>
          ))
        ) : (
          clientGroups.map(g => (
            <Group
              key={g.id}
              title={g.name}
              count={g.entries.length}
              icon={clientById.get(g.id) && <ClientBadge client={clientById.get(g.id)!} size="sm" />}
            >
              {g.entries.map(entry => (
                <EntryRow
                  key={entry.id}
                  entry={entry}
                  meta={formatDayLong(entry.date)}
                  {...rowProps}
                />
              ))}
            </Group>
          ))
        )}
          </>
        )}
      </main>
    </div>
  );
}

const logoUrl = (c: Pick<Client, "id" | "logo_version">) =>
  c.logo_version ? `/api/clients/${c.id}/logo?v=${c.logo_version}` : null;

// Logo varsa logo, yoksa müşteri renginde baş harf
function ClientBadge({ client, size = "md" }: { client: Client; size?: "sm" | "md" }) {
  const box = size === "sm" ? "size-5 text-[10px]" : "size-10 text-sm";
  const url = logoUrl(client);
  if (url) {
    return (
      <img
        src={url}
        alt=""
        className={`${box} shrink-0 rounded-md border border-slate-200 bg-white object-contain p-0.5`}
      />
    );
  }
  return (
    <span
      className={`${box} grid shrink-0 place-items-center rounded-md font-semibold text-white`}
      style={{ backgroundColor: client.color || DEFAULT_ACCENT }}
    >
      {client.name.charAt(0).toLocaleUpperCase("tr-TR")}
    </span>
  );
}

function ClientManager({ clients, onChange }: { clients: Client[]; onChange: () => Promise<void> }) {
  const [name, setName] = useState("");
  const [editId, setEditId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await onChange();
      return true;
    } catch (e) {
      setError((e as Error).message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (await run(() => api("/api/clients", { method: "POST", body: JSON.stringify({ name }) }))) setName("");
  };

  const remove = (c: Client) => {
    const msg =
      c.entry_count > 0
        ? `"${c.name}" silinsin mi?\n\nBu müşteriye ait ${c.entry_count} kayıt da kalıcı olarak silinecek.`
        : `"${c.name}" silinsin mi?`;
    if (window.confirm(msg)) run(() => api(`/api/clients/${c.id}`, { method: "DELETE" }));
  };

  return (
    <div className="space-y-4">
      {error && <ErrorBox>{error}</ErrorBox>}
      <form onSubmit={add} className="flex gap-2">
        <input
          required
          data-autofocus
          placeholder="Yeni müşteri adı"
          value={name}
          onChange={e => setName(e.target.value)}
          className="input"
        />
        <button type="submit" disabled={busy} className="btn-primary shrink-0">
          Ekle
        </button>
      </form>

      {clients.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-500">Henüz müşteri eklenmedi.</p>
      ) : (
        <ul className="max-h-[60vh] divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200">
          {clients.map(c => (
            <li key={c.id} className="px-3 py-2.5">
              {editId === c.id ? (
                <ClientEditor client={c} busy={busy} run={run} onDone={() => setEditId(null)} />
              ) : (
                <div className="flex items-center gap-3">
                  <ClientBadge client={c} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{c.name}</p>
                    <p className="flex items-center gap-1.5 text-xs text-slate-500">
                      {c.entry_count} kayıt
                      {c.color && (
                        <>
                          <span>·</span>
                          <span className="size-2.5 rounded-full" style={{ backgroundColor: c.color }} />
                          <span className="font-mono">{c.color}</span>
                        </>
                      )}
                    </p>
                  </div>
                  <button onClick={() => setEditId(c.id)} className="btn-ghost text-xs">
                    Düzenle
                  </button>
                  <button onClick={() => remove(c)} disabled={busy} className="btn-ghost text-xs text-red-600">
                    Sil
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const HEX_RE = /^#[0-9a-f]{6}$/i;

function ClientEditor({
  client,
  busy,
  run,
  onDone,
}: {
  client: Client;
  busy: boolean;
  run: (fn: () => Promise<unknown>) => Promise<boolean>;
  onDone: () => void;
}) {
  const [name, setName] = useState(client.name);
  const [color, setColor] = useState(client.color ?? "");
  const fileRef = useRef<HTMLInputElement>(null);
  const validColor = HEX_RE.test(color);

  const save = async (e: FormEvent) => {
    e.preventDefault();
    const ok = await run(() =>
      api(`/api/clients/${client.id}`, { method: "PUT", body: JSON.stringify({ name, color: color || null }) }),
    );
    if (ok) onDone();
  };

  const upload = async (file: File | undefined) => {
    if (!file) return;
    const body = new FormData();
    body.append("file", file);
    // Content-Type'ı tarayıcı (multipart sınırıyla) kendisi koymalı
    await run(async () => {
      const res = await fetch(`/api/clients/${client.id}/logo`, { method: "PUT", body });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error ?? "Logo yüklenemedi.");
    });
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeLogo = () => run(() => api(`/api/clients/${client.id}/logo`, { method: "DELETE" }));

  return (
    <form
      onSubmit={save}
      onKeyDown={e => {
        // Esc sadece düzenlemeyi iptal etsin, modalı kapatmasın
        if (e.key === "Escape") {
          e.preventDefault();
          onDone();
        }
      }}
      className="space-y-3"
    >
      <Field label="Ad">
        <input required autoFocus value={name} onChange={e => setName(e.target.value)} className="input" />
      </Field>

      <Field label="Renk" action={color && <ClearButton onClick={() => setColor("")}>Rengi kaldır</ClearButton>}>
        <div className="flex gap-2">
          <input
            type="color"
            value={validColor ? color : DEFAULT_ACCENT}
            onChange={e => setColor(e.target.value)}
            className="h-9 w-12 shrink-0 cursor-pointer rounded-lg border border-slate-300 bg-white p-1"
            aria-label="Renk seç"
          />
          <input
            placeholder={`${DEFAULT_ACCENT} (varsayılan)`}
            value={color}
            onChange={e => {
              const v = e.target.value.trim();
              setColor(v && !v.startsWith("#") ? `#${v}` : v);
            }}
            maxLength={7}
            className={`input font-mono ${color && !validColor ? "border-red-400 focus:border-red-500" : ""}`}
          />
        </div>
      </Field>

      <Field
        label="Logo"
        action={client.logo_version && <ClearButton onClick={removeLogo}>Logoyu kaldır</ClearButton>}
      >
        <div className="flex items-center gap-3">
          <ClientBadge client={{ ...client, color: validColor ? color : client.color }} />
          <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} className="btn-secondary py-1.5">
            {client.logo_version ? "Değiştir" : "Logo yükle"}
          </button>
          <span className="text-xs text-slate-500">PNG, JPG, WEBP, SVG · en fazla 2 MB</span>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            onChange={e => upload(e.target.files?.[0])}
            className="hidden"
          />
        </div>
      </Field>

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onDone} className="btn-ghost text-sm">
          Vazgeç
        </button>
        <button type="submit" disabled={busy || (color !== "" && !validColor)} className="btn-primary py-1.5">
          Kaydet
        </button>
      </div>
    </form>
  );
}

// Yazdıkça (kısa gecikmeyle) adresteki ?ara= güncellenir
function SearchBox({ value, onChange }: { value: string; onChange: (q: string) => void }) {
  const [text, setText] = useState(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // Geri/ileri ile adres değişince kutu da güncellensin
  useEffect(() => setText(value), [value]);

  useEffect(() => {
    if (text.trim() === value) return;
    const t = setTimeout(() => onChangeRef.current(text.trim()), 300);
    return () => clearTimeout(t);
  }, [text, value]);

  return (
    <div className="relative">
      <svg
        viewBox="0 0 20 20"
        fill="currentColor"
        className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400"
        aria-hidden
      >
        <path
          fillRule="evenodd"
          d="M9 3.5a5.5 5.5 0 1 0 3.4 9.83l3.63 3.64a.75.75 0 1 0 1.06-1.06l-3.63-3.64A5.5 5.5 0 0 0 9 3.5ZM5 9a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z"
          clipRule="evenodd"
        />
      </svg>
      <input
        type="search"
        placeholder="Tüm kayıtlarda ara… (başlık, açıklama, müşteri)"
        value={text}
        onChange={e => setText(e.target.value)}
        onKeyDown={e => e.key === "Escape" && setText("")}
        className="input py-2.5 pl-9"
      />
    </div>
  );
}

function BackupPanel({ onRestored }: { onRestored: () => Promise<void> }) {
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const downloadCurrent = () => {
    const a = document.createElement("a");
    a.href = "/api/backup";
    a.download = "";
    a.click();
  };

  const restore = async (file: File | undefined) => {
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setStatus(null);
    let data: { app?: string; exported_at?: string; clients?: unknown[]; entries?: unknown[] };
    try {
      data = JSON.parse(await file.text());
    } catch {
      setStatus({ ok: false, text: "Dosya okunamadı; geçerli bir yedek dosyası seçin." });
      return;
    }
    if (data.app !== "msy-rapor-sistemi" || !Array.isArray(data.clients) || !Array.isArray(data.entries)) {
      setStatus({ ok: false, text: "Bu dosya geçerli bir rapor yedeği değil." });
      return;
    }
    const when = data.exported_at ? new Date(data.exported_at).toLocaleString("tr-TR") : "bilinmiyor";
    const ok = window.confirm(
      `Yedek tarihi: ${when}\n${data.clients.length} müşteri, ${data.entries.length} kayıt\n\n` +
        "Mevcut TÜM veri silinip bu yedekle değiştirilecek.\n" +
        "Güvenlik için önce mevcut verinin yedeği indirilecek. Devam edilsin mi?",
    );
    if (!ok) return;
    setBusy(true);
    try {
      downloadCurrent();
      const res = await api<{ clients: number; entries: number }>("/api/backup", {
        method: "POST",
        body: JSON.stringify(data),
      });
      await onRestored();
      setStatus({ ok: true, text: `Geri yüklendi: ${res.clients} müşteri, ${res.entries} kayıt.` });
    } catch (e) {
      setStatus({ ok: false, text: `Geri yüklenemedi, mevcut veri değişmedi: ${(e as Error).message}` });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 text-sm">
      {status &&
        (status.ok ? (
          <p className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-green-800">{status.text}</p>
        ) : (
          <ErrorBox>{status.text}</ErrorBox>
        ))}
      <div className="rounded-lg border border-slate-200 p-4">
        <p className="font-medium">Yedek indir</p>
        <p className="mt-1 text-slate-500">Tüm müşteriler, logolar ve kayıtlar tek bir .json dosyasına kaydedilir.</p>
        <button data-autofocus onClick={downloadCurrent} className="btn-primary mt-3">
          Yedek indir
        </button>
      </div>
      <div className="rounded-lg border border-red-200 bg-red-50/40 p-4">
        <p className="font-medium">Yedekten geri yükle</p>
        <p className="mt-1 text-slate-600">
          Mevcut <strong>tüm veri</strong> seçilen yedekle değiştirilir. Başlamadan önce mevcut verinin yedeği otomatik
          indirilir; bir hata olursa hiçbir şey değişmez.
        </p>
        <button onClick={() => fileRef.current?.click()} disabled={busy} className="btn-secondary mt-3">
          {busy ? "Geri yükleniyor…" : "Yedek dosyası seç…"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          onChange={e => restore(e.target.files?.[0])}
          className="hidden"
        />
      </div>
    </div>
  );
}

function ClearButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="text-xs font-medium text-slate-500 hover:text-red-600">
      {children}
    </button>
  );
}

function EntryRow({
  entry,
  meta,
  onEdit,
  onCopy,
  onDelete,
}: {
  entry: Entry;
  meta: string;
  onEdit: (e: Entry) => void;
  onCopy: (e: Entry) => void;
  onDelete: (e: Entry) => void;
}) {
  return (
    <li className="group px-4 py-3">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <span className="text-xs font-semibold text-blue-700">{meta}</span>
          <p className="font-medium">{entry.title}</p>
          {entry.description && (
            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-600">{entry.description}</p>
          )}
        </div>
        <div className="flex shrink-0 gap-1 opacity-60 transition group-hover:opacity-100">
          <button onClick={() => onEdit(entry)} className="btn-ghost text-xs">
            Düzenle
          </button>
          <button onClick={() => onCopy(entry)} className="btn-ghost text-xs" title="Bugüne kopyala">
            Kopyala
          </button>
          <button onClick={() => onDelete(entry)} className="btn-ghost text-xs text-red-600">
            Sil
          </button>
        </div>
      </div>
    </li>
  );
}

function Group({
  title,
  count,
  icon,
  children,
}: {
  title: string;
  count: number;
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <h3 className="flex items-center gap-2 border-b border-slate-100 px-4 py-3 text-sm font-semibold text-slate-700">
        {icon}
        <span className="min-w-0 flex-1 truncate">{title}</span>
        <span className="shrink-0 text-xs font-medium text-slate-400">{count} kayıt</span>
      </h3>
      <ul className="divide-y divide-slate-100">{children}</ul>
    </div>
  );
}

const segmentClass = (active: boolean) =>
  `rounded-md px-3 py-1.5 text-sm font-medium transition ${
    active ? "bg-white text-blue-700 shadow-sm" : "text-slate-600 hover:text-slate-900"
  }`;

function Segmented<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (v: T) => void;
  options: [T, string][];
}) {
  return (
    <div className="inline-flex rounded-lg bg-slate-100 p-1">
      {options.map(([v, label]) => (
        <button key={v} onClick={() => onChange(v)} className={segmentClass(value === v)}>
          {label}
        </button>
      ))}
    </div>
  );
}

function Modal({
  open,
  onClose,
  title,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
      dialog.querySelector<HTMLElement>("[data-autofocus]")?.focus();
    }
    if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      // Arka plana (backdrop) tıklayınca kapanır
      onClick={e => e.target === ref.current && onClose()}
      className="modal m-auto w-[calc(100%-2rem)] max-w-lg rounded-xl border border-slate-200 bg-white p-0 text-slate-900 shadow-xl"
    >
      <div className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="btn-icon" aria-label="Kapat">
            ×
          </button>
        </div>
        {children}
      </div>
    </dialog>
  );
}

function Field({ label, action, children }: { label: string; action?: ReactNode; children: ReactNode }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-sm font-medium text-slate-700">{label}</span>
        {action}
      </div>
      {children}
    </div>
  );
}

function ErrorBox({ children }: { children: ReactNode }) {
  return <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{children}</p>;
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2">
      <div className="text-xl font-semibold text-blue-700">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}

export default App;
